import { useEffect, useState } from 'react';
import { RefreshCw, Activity, AlertCircle, CheckCircle, Clock, PlayCircle } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  NotificationCard,
  UniversalLoadingState,
  StatusBadge,
  useToast,
  type StatusTone,
} from '@sighthost/ui';
import {
  adminCronService,
  type CronHealthRow,
  type CronLivenessRow,
} from '@/domains/admin/services/adminCronService';
import { formatDateTime, relativeTime } from '@/shared/utils/format';
import { ConfirmDialog } from '@/shared/components/feedback/ConfirmDialog';

/**
 * CronHealthPage — Sprint Admin-A A-3.
 *
 * Lists all registered pg_cron jobs with their last-run metadata + a "Run now"
 * button for allowlisted jobs. Reads admin_cron_health() (SECURITY DEFINER,
 * is_staff-fenced).
 *
 * Spec: _bmad-output/ADMIN_PORTAL_GAP_ANALYSIS_2026-05-13.md §6 A-3.
 * Arch lock Q5: per-jobname allowlist for manual triggers.
 */

const STATUS_TONE: Record<string, StatusTone> = {
  succeeded: 'active',
  failed: 'overdue',
  running: 'pending',
  starting: 'pending',
};

export default function CronHealthPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<CronHealthRow[] | null>(null);
  const [liveness, setLiveness] = useState<CronLivenessRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [busyJob, setBusyJob] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);

  async function load() {
    try {
      setRefreshing(true);
      // Both signals, together — see the banner below for why one is not enough.
      const [data, live] = await Promise.all([
        adminCronService.list(),
        adminCronService.liveness(),
      ]);
      setRows(data);
      setLiveness(live);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  }

  const livenessByName = new Map(liveness.map((l) => [l.job_name, l]));

  useEffect(() => {
    load();
  }, []);

  async function onTrigger(jobname: string) {
    setBusyJob(jobname);
    try {
      const result = await adminCronService.trigger(jobname);
      toast({ tone: 'success', title: 'Trigger requested', body: result.note });
      await load();
    } catch (e: unknown) {
      toast({
        tone: 'danger',
        title: 'Could not trigger',
        body: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusyJob(null);
    }
  }

  return (
    <UniversalPage
      title="Cron health"
      description="Background runners. Refreshes on demand."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Ops', href: '/admin/ops/audit' },
        { label: 'Cron health' },
      ]}
      actions={
        <Button variant="marketing" size="sm" onClick={load} disabled={refreshing}>
          <RefreshCw className={`mr-1 size-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      }
    >
      {error && <NotificationCard tone="danger" title="Could not load cron health" body={error} />}
      {!rows && !error && <UniversalLoadingState variant="rows" count={6} />}

      {/* F4b (2026-08-18): this page showed ONLY pg_cron's run status, which is
          the signal CLAUDE.md §4.8 exists to warn about — it reads 'succeeded'
          when the gateway answered 401 or 500. That is why 16 days of dead
          billing in June and 18 in July were invisible from here. The heartbeat
          signal now sits beside it, and leads. */}
      {rows && (
        <NotificationCard
          tone="info"
          title="Two signals, and only one of them proves the job ran"
          body={
            <>
              <strong>Ran</strong> is the one to trust: the function wrote its own heartbeat or audit
              event. <strong>Gateway</strong> is pg_cron&rsquo;s HTTP result — it records{' '}
              <em>succeeded</em> even when the platform returned 401 or 500 without the function ever
              executing. A job can show <em>succeeded</em> here and be completely dead. Jobs that run
              inside the database (the watchdog, the run-details prune) have no heartbeat and are
              shown as not monitored.
            </>
          }
        />
      )}

      {rows && rows.length === 0 && (
        <Card interactive={false} className="p-4 text-sm text-muted-foreground">
          No cron jobs registered. Check supabase/migrations for `cron.schedule(...)` calls.
        </Card>
      )}

      {rows && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((r) => {
            const tone = r.last_run_status
              ? STATUS_TONE[r.last_run_status] ?? 'draft'
              : 'draft';
            const live = livenessByName.get(r.jobname);
            // The heartbeat outranks the gateway result. A job whose function
            // has gone silent is a failure even while pg_cron says 'succeeded'
            // — that combination IS the silent-cron outage signature.
            const silent = live?.is_stale === true;
            const statusIcon = silent ? (
              <AlertCircle className="size-4 text-danger" aria-hidden />
            ) : r.last_run_status === 'succeeded' ? (
              <CheckCircle className="size-4 text-success" aria-hidden />
            ) : r.last_run_status === 'failed' ? (
              <AlertCircle className="size-4 text-danger" aria-hidden />
            ) : (
              <Clock className="size-4 text-muted-foreground" aria-hidden />
            );

            return (
              <Card key={r.jobid} interactive={false} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {statusIcon}
                      <span className="font-mono text-sm font-semibold text-foreground">{r.jobname}</span>
                      {!r.active && <StatusBadge tone="draft">Inactive</StatusBadge>}
                      {silent && <StatusBadge tone="overdue">Silent — did not run</StatusBadge>}
                      {r.last_run_status && (
                        <StatusBadge tone={tone}>gateway: {r.last_run_status}</StatusBadge>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Schedule <code className="rounded-sm bg-card-subtle px-1">{r.schedule}</code>
                      {' · '}
                      {/* A7 (2026-08-03): admin_cron_health() reads only the last
                          50,000 runids (~17 days) — cron.job_run_details cannot be
                          indexed, so an unbounded scan is not safe. A NULL here means
                          "nothing in that window", NOT "never run". Say so: a job idle
                          for 17 days is exactly what this page exists to surface. */}
                      {r.last_run_start ? (
                        <>last run {relativeTime(r.last_run_start)} ({formatDateTime(r.last_run_start)})</>
                      ) : (
                        <span className="font-medium text-warning">no runs in the last ~17 days</span>
                      )}
                    </p>

                    {/* The trustworthy signal (F4b). `last_seen` is
                        GREATEST(audit event, heartbeat beat) — daily jobs write
                        an audit row, minute runners beat instead. */}
                    <p className="mt-1 text-xs">
                      <span className="font-medium text-foreground">Ran:</span>{' '}
                      {!live ? (
                        <span className="text-muted-foreground">
                          not monitored — no heartbeat registered
                        </span>
                      ) : live.last_seen ? (
                        <>
                          <span className={silent ? 'font-semibold text-danger' : 'text-success'}>
                            {relativeTime(live.last_seen)}
                          </span>{' '}
                          <span className="text-muted-foreground">
                            ({formatDateTime(live.last_seen)} · alarms after {live.max_age_hours}h ·{' '}
                            <code className="rounded-sm bg-card-subtle px-1">{live.audit_event}</code>)
                          </span>
                        </>
                      ) : (
                        <span className="font-semibold text-danger">
                          never — no heartbeat or audit event on record
                        </span>
                      )}
                    </p>
                    {silent && (
                      <p className="mt-1 text-xs font-medium text-danger">
                        This job has not actually run inside its {live?.max_age_hours}h window.
                        support@ has been alerted by the hourly watchdog.
                      </p>
                    )}

                    {r.last_run_error && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs font-medium text-danger hover:underline">
                          Last error — click to expand
                        </summary>
                        <pre className="mt-1 max-h-32 overflow-auto rounded-sm bg-card-subtle p-2 font-mono text-xs text-danger">
                          {r.last_run_error}
                        </pre>
                      </details>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {r.in_allowlist ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busyJob === r.jobname}
                        loading={busyJob === r.jobname}
                        onClick={() => setTriggering(r.jobname)}
                      >
                        <PlayCircle className="mr-1 size-4" aria-hidden />
                        Run now
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">Manual trigger blocked</span>
                    )}
                  </div>
                </div>

                {r.recent_5_runs.length > 1 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:underline">
                      Recent runs ({r.recent_5_runs.length})
                    </summary>
                    <ul className="mt-2 space-y-1">
                      {r.recent_5_runs.map((run, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between gap-2 rounded-sm bg-card-subtle px-2 py-1 text-xs"
                        >
                          <span className="font-mono text-foreground">{formatDateTime(run.start_time)}</span>
                          <StatusBadge tone={STATUS_TONE[run.status] ?? 'draft'}>{run.status}</StatusBadge>
                          {run.return_message && (
                            <span className="truncate text-muted-foreground" title={run.return_message}>
                              {run.return_message}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Card interactive={false} className="mt-4 border-info/30 bg-info/5 p-3 text-xs">
        <p className="text-foreground">
          <Activity className="mr-1 inline size-3" aria-hidden /> Manual-trigger allowlist lives in{' '}
          <code>system_setting.cron_manual_trigger_allowlist</code>. <code>billing-cron-daily</code> and{' '}
          <code>payment-reconciliation-daily</code> are intentionally blocked from manual fire to prevent wrong-dated
          rows.
        </p>
      </Card>

      <ConfirmDialog
        open={triggering !== null}
        onOpenChange={(v) => !v && setTriggering(null)}
        title="Run this job now?"
        description={
          triggering
            ? `${triggering} will be requested to fire on its next scheduled tick. It runs against production data.`
            : undefined
        }
        confirmLabel="Request run"
        busy={busyJob !== null}
        onConfirm={() => {
          if (triggering) {
            const j = triggering;
            setTriggering(null);
            void onTrigger(j);
          }
        }}
      />
    </UniversalPage>
  );
}
